import React from 'react';

import { cn, FOCUS_RING_ITEM } from '../lib/utils';

export interface DialogSection<T extends string> {
  value: T;
  label: string;
  /**
   * Right-aligned count. `null` means "not known yet" and renders nothing,
   * which is what a section whose data loads asynchronously wants — a `0`
   * standing in for "still loading" is a lie the reader acts on.
   */
  count?: number | null;
  /** A small warning dot before the count: something in here needs a person. */
  attention?: boolean;
  /** One quiet line under the entry, saying what the dot is about. */
  note?: string;
}

interface DialogSectionRailProps<T extends string> {
  /** Names the nav for assistive technology, e.g. "Template sections". */
  label: string;
  sections: Array<DialogSection<T>>;
  value: T;
  onChange: (value: T) => void;
}

/**
 * The left rail inside a record dialog — the one that reads and edits a single
 * object across a couple of sections (see DESIGN_SYSTEM.md, "Record workbench").
 *
 * TWO THINGS IT OWNS, and they are the reason this is a component rather than a
 * block of classes copied between dialogs.
 *
 * It renders NOTHING below two sections. A rail with one entry is furniture: it
 * navigates nowhere and costs 208 pixels to say so. Sections disappear with the
 * permissions that reach them, so "how many are there" is a runtime question and
 * every caller would otherwise have to remember to ask it.
 *
 * And it carries STATE, not only navigation. A count and a needs-attention dot
 * turn two labels into the summary the reader would otherwise have to click
 * each section to discover — which is what earns the space the rail takes.
 * That state is TEXT in the button's name as well ("Versions, needs attention,
 * 3"), and the note is linked with aria-describedby, because a dot alone says
 * nothing to a screen reader (DESIGN_SYSTEM.md §8.3).
 *
 * It reflows to a horizontal strip below `sm` rather than being duplicated by
 * one: two controls with the same name for the same destination is a thing
 * screen-reader users have to disambiguate for no reason.
 */
export function DialogSectionRail<T extends string>({
  label,
  sections,
  value,
  onChange,
}: DialogSectionRailProps<T>): React.ReactElement | null {
  // Above the early return: hooks run on every render.
  const baseId = React.useId();
  if (sections.length < 2) return null;

  return (
    <nav
      aria-label={label}
      className="flex shrink-0 gap-1 overflow-x-auto border-b p-2 sm:flex-col sm:overflow-x-visible sm:overflow-y-auto sm:border-b-0 sm:border-r sm:bg-muted/30 sm:p-3"
    >
      {sections.map((section) => {
        const isCurrent = section.value === value;
        const noteId = section.note ? `${baseId}-${section.value}-note` : undefined;
        return (
          <React.Fragment key={section.value}>
            <button
              type="button"
              onClick={() => onChange(section.value)}
              aria-current={isCurrent ? 'page' : undefined}
              aria-describedby={noteId}
              className={cn(
                'relative flex shrink-0 items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors sm:w-full',
                FOCUS_RING_ITEM,
                isCurrent
                  ? 'bg-primary/10 font-medium text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {isCurrent ? (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary"
                />
              ) : null}
              <span>{section.label}</span>
              {section.attention || section.count != null ? (
                <span className="flex items-center gap-1.5">
                  {section.attention ? (
                    <>
                      <span
                        aria-hidden="true"
                        className="h-1.5 w-1.5 rounded-full bg-warning-emphasis"
                      />
                      <span className="sr-only">, needs attention</span>
                    </>
                  ) : null}
                  {section.count != null ? (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      <span className="sr-only">, </span>
                      {section.count}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </button>

            {/* Below `sm`, where the rail is a row, the note is hidden; the
                button still carries it through aria-describedby. */}
            {section.note ? (
              <p id={noteId} className="hidden px-3 pt-0.5 text-xs text-muted-foreground sm:block">
                {section.note}
              </p>
            ) : null}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default DialogSectionRail;
